import {
  type HTMLAttributes,
  type Ref,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import throttle from "lodash/throttle";

type Props = {
  /**
   * Color at the bottom of the gradient
   */
  toColor?: "white" | "grey";
  /**
   * Initial state of container
   */
  expand?: boolean;
  /**
   * Initial max-height of unexpanded component
   */
  initialHeight?: string;
  /**
   * Max-height of the component when expanded
   */
  maxHeight?: string;
  /**
   * Click event that returns the event and boolean for the
   * expanded/unexpanded container state.
   */
  onButtonClick?: (e: React.MouseEvent, isExpanded?: boolean) => void;
  ref?: Ref<HTMLDivElement>;
} & HTMLAttributes<HTMLDivElement>;

const ExpandableContainer = ({
  className,
  toColor = "white",
  children,
  expand = false,
  initialHeight = "188px",
  maxHeight = "9999px",
  onButtonClick,
  ref,
  ...paragraphProps
}: Props) => {
  const generatedId = useId();

  const [expandedContent, setExpandedContent] = useState(expand);
  const [hideButton, setHideButton] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const shouldHideButton = throttle(() => {
      const childHeight = containerRef.current?.clientHeight || 0;
      const containerHeight = parseInt(initialHeight, 10);

      const isChildHeightShort = childHeight < containerHeight;
      if (hideButton !== isChildHeightShort) {
        setHideButton(isChildHeightShort);
      }
    }, 200);

    window.addEventListener("resize", shouldHideButton);

    shouldHideButton();

    return () => {
      window.removeEventListener("resize", shouldHideButton);
    };
  }, [initialHeight, hideButton]);

  return (
    <div className={className} ref={ref}>
      <div
        data-testid="expandable-container-text-content"
        ref={containerRef}
        id={generatedId}
        className={classNames("mb-0", {
          // using auto in the event that `maxHeight` is smaller than content height, we'll get a scrollbar
          "overflow-y-auto": expandedContent,
          "overflow-y-hidden": !expandedContent,
        })}
        style={{ maxHeight: expandedContent ? maxHeight : initialHeight }}
        {...paragraphProps}
      >
        {children}
      </div>
      {!hideButton && (
        <button
          data-testid="expandable-container-button"
          className={classNames(
            // text styles
            "text-xs font-bold underline w-full text-left relative",
            // gradient setup
            "before:absolute before:-top-7 before:w-full before:z-10 before:content-[''] before:h-7",
            {
              // gradient styling
              "before:bg-gradient-to-b before:from-transparent before:to-75%":
                !expandedContent,
              "before:to-leafly-white": !expandedContent && toColor === "grey",
              "before:to-white": !expandedContent && toColor === "white",
            },
          )}
          onClick={(e: React.MouseEvent) => {
            // Call the onButtonClick prop with the new state
            onButtonClick?.(e, !expandedContent);
            setExpandedContent(!expandedContent);
          }}
          aria-expanded={expandedContent}
          aria-controls={containerRef.current?.id}
        >
          {expandedContent ? "show less" : "show more"}
        </button>
      )}
    </div>
  );
};

export default ExpandableContainer;
